<template>
  <div class="news-illustration" :class="[typeClass, variantClass, sizeClass]">
    <img
      :src="illustrationSrc"
      :alt="altText"
      :loading="loadingMode"
      decoding="async"
      class="illustration-image"
    >
    <div class="illustration-shade"></div>
    <div class="illustration-highlight"></div>
  </div>
</template>

<script setup>
const props = defineProps({
  type: {
    type: String,
    default: 'default',
    validator: (value) => ['company', 'product', 'industry', 'tech', 'default'].includes(value)
  },
  variant: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  }
})

const resolvedKey = computed(() => props.variant || props.type || 'default')
const typeClass = computed(() => `type-${props.type || 'default'}`)
const variantClass = computed(() => (props.variant ? `variant-${props.variant}` : ''))
const sizeClass = computed(() => `size-${props.size}`)

const illustrations = {
  company: '/visuals/news/company.svg',
  product: '/visuals/news/product.svg',
  industry: '/visuals/news/industry.svg',
  tech: '/visuals/news/tech.svg',
  partnership: '/visuals/news/partnership.svg',
  optimization: '/visuals/news/optimization.svg',
  profile: '/visuals/news/profile.svg',
  compliance: '/visuals/news/compliance.svg',
  mobile: '/visuals/news/mobile.svg',
  'private-domain': '/visuals/news/private-domain.svg',
  default: '/visuals/news/default.svg'
}

const altLabels = {
  company: '公司动态插画',
  product: '产品更新插画',
  industry: '行业洞察插画',
  tech: '技术分享插画',
  partnership: '战略合作插画',
  optimization: '算法优化插画',
  profile: '用户画像插画',
  compliance: '跨境合规插画',
  mobile: '移动广告插画',
  'private-domain': '私域运营插画',
  default: '新闻插画'
}

const illustrationSrc = computed(() => illustrations[resolvedKey.value] || illustrations[props.type] || illustrations.default)
const altText = computed(() => altLabels[resolvedKey.value] || altLabels[props.type] || altLabels.default)
const loadingMode = computed(() => (props.size === 'lg' ? 'eager' : 'lazy'))
</script>

<style scoped>
.news-illustration {
  position: relative;
  overflow: hidden;
  border-radius: 1rem;
  border: 1px solid rgba(99, 102, 241, 0.28);
  background: linear-gradient(145deg, rgba(15, 23, 42, 0.82), rgba(30, 58, 138, 0.56));
  box-shadow: 0 16px 42px rgba(2, 6, 23, 0.32);
  isolation: isolate;
}

.illustration-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.03);
  animation: illustration-drift 16s ease-in-out infinite alternate;
}

.illustration-shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(15, 23, 42, 0.2) 100%),
    radial-gradient(circle at 12% 8%, rgba(56, 189, 248, 0.14), transparent 40%);
  pointer-events: none;
}

.illustration-highlight {
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, rgba(255, 255, 255, 0.18), transparent 32%, transparent 68%, rgba(167, 139, 250, 0.14));
  mix-blend-mode: screen;
  opacity: 0.45;
  pointer-events: none;
}

.size-sm {
  width: 88px;
  height: 88px;
  border-radius: 0.85rem;
}

.size-md {
  width: 100%;
  height: 190px;
}

.size-lg {
  width: 100%;
  height: clamp(220px, 31vw, 340px);
}

.type-company .illustration-image {
  animation-duration: 18s;
}

.type-tech .illustration-image {
  animation-duration: 14s;
}

.dark .news-illustration {
  border-color: rgba(125, 211, 252, 0.22);
}

.light .news-illustration {
  border-color: rgba(59, 130, 246, 0.24);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(224, 242, 254, 0.78));
  box-shadow: 0 16px 38px rgba(30, 64, 175, 0.14);
}

.light .illustration-shade {
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(15, 23, 42, 0.04) 100%),
    radial-gradient(circle at 10% 10%, rgba(56, 189, 248, 0.12), transparent 40%);
}

@keyframes illustration-drift {
  0% {
    transform: translate3d(0, 0, 0) scale(1.03);
  }
  50% {
    transform: translate3d(-1.2%, 1.4%, 0) scale(1.05);
  }
  100% {
    transform: translate3d(1.4%, -1.2%, 0) scale(1.03);
  }
}

@media (max-width: 768px) {
  .size-md {
    height: 170px;
  }

  .size-lg {
    height: clamp(210px, 56vw, 300px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .illustration-image {
    animation: none;
    transform: scale(1.01);
  }
}
</style>
